import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiClient } from '../api/client';
import { useTranslation } from '../i18n';
import WebApp from '@twa-dev/sdk';
import { AdminPanel } from './admin/AdminPanel';

interface UserInfo {
  id: number;
  username: string;
  first_name: string;
  last_name?: string;
  photo_url?: string;
  activated: boolean;
  balance: number;
}

interface ProfileProps {
  userInfo: UserInfo;
}

export function Profile({ userInfo }: ProfileProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleNameClick = () => {
    const newClickCount = clickCount + 1;
    setClickCount(newClickCount);
    if (newClickCount >= 5) {
      setShowAdminPanel(!showAdminPanel);
      setClickCount(0); // Reset click count after toggling
    }
  };

  const handleGenerateInvitation = async () => {
    setIsGenerating(true);
    try {
      const invitation = await ApiClient.generateInvitation();
      
      const botUsername = import.meta.env.VITE_BOT_USERNAME;
      const miniAppName = import.meta.env.VITE_MINI_APP_NAME;
      const inviteLink = `t.me/${botUsername}/${miniAppName}?startapp=invite_${invitation.id}`;
      
      const message = t('invitation.shareMessage');
      
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(inviteLink)}&text=${encodeURIComponent(message)}`;
      
      WebApp.openTelegramLink(shareUrl);
    } catch (error) {
      console.error(t('errors.generateInvitation'), error);
      // You might want to show an error message to the user
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  return (
    <div className="profile">
      <img 
        src={userInfo.photo_url || '/default-avatar.png'} 
        alt={userInfo.first_name} 
        className="avatar"
        onError={(e) => {
          e.currentTarget.src = '/default-avatar.png';
        }}
      />
      <h1 className="name" onClick={handleNameClick}>
        {userInfo.first_name} {userInfo.last_name}
      </h1>
      {userInfo.username && (
        <p className="username">@{userInfo.username}</p>
      )}
      <div className="flex items-center gap-2 mt-2">
        <span className="text-theme-hint">{t('profile.balanceLabel')} <span className="text-lg">✨</span>{userInfo.balance}</span>
      </div>

      {/* Settings Link */}
      <div className="mt-6 w-full">
        <button 
          onClick={handleSettingsClick}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg transition-all hover:bg-black/5 dark:hover:bg-white/5 text-theme-text"
        >
          <span className="material-symbols-outlined">settings</span>
          <span className="font-medium">{t('profile.settingsButton')}</span>
        </button>
      </div>

      {/* Invitation Section */}
      <div className="mt-8 w-full">
        <h2 className="text-xl font-semibold text-theme-text mb-2">{t('invitation.title')}</h2>
        <p className="text-theme-hint mb-4">{t('invitation.description')}</p>
        <button 
          onClick={handleGenerateInvitation}
          disabled={isGenerating}
          className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none"
          style={{
            backgroundColor: 'var(--color-button-bg)',
            color: 'var(--color-button-text)',
          }}
        >
          <span className="material-symbols-outlined">share</span>
          {isGenerating ? t('invitation.generatingButtonText') : t('invitation.shareButtonText')}
        </button>
      </div>
      {showAdminPanel && (
        <AdminPanel />
      )}
    </div>
  );
} 